import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from "@angular/fire/firestore";
import { AngularFireFunctions } from "@angular/fire/functions";
import { map } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { User } from "../@core/data/users";
import { UserService } from "../@core/services/user.service";
import Quiz, { QuizAnswer } from "../models/quiz.model";
import * as firebase from "firebase/app";
import "firebase/firestore";

@Injectable({
  providedIn: "root",
})
export class QuizContentService {
  visibility = environment.visibility;
  baseUrl = environment.apiUrl;
  functionsUrl = environment.functionsUrl;

  userLogin: User;
  private dbPath = "/quizzes";
  contentRef: AngularFirestoreCollection<Quiz>;

  constructor(
    private db: AngularFirestore,
    private userStore: UserService,
    private fns: AngularFireFunctions,
    private http: HttpClient
  ) {
    this.contentRef = db.collection(this.dbPath);

    this.userStore.getCurrentUser().subscribe((user: User) => {
      this.userLogin = user;
    });
  }

  getFsPublishedQuizzes(uid: string, limit?: number, cursor?: any): any {
    var db = firebase.default.firestore();
    var ref = db
      .collection(this.dbPath)
      .where("isDeleted", "==", false)
      .where("isActive", "==", true)
      .where("published", "==", true)
      .where("privacy", "==", false);

    if (uid != "") {
      ref = ref.where("createdBy", "==", uid);
    }

    ref = ref.orderBy("updatedOn", "desc");

    ref = limit ? ref.limit(limit) : ref.limit(10);

    if (cursor) {
      ref = ref.startAfter(cursor);
    }

    return ref.get();
  }

  getQuizzes(userId?: string): AngularFirestoreCollection<Quiz> {
    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("createdBy", "==", userId)
        .orderBy("updatedOn", "desc")
    );
  }

  get(id: string) {
    return this.contentRef.doc(id).valueChanges();
  }

  getQuizById(id: string) {
    var db = firebase.default.firestore();
    var ref = db.collection(this.dbPath).doc(id);

    return ref.get();
  }

  create(content: Quiz): any {
    content.tags = content.tags ? content.tags : [];
    content.answers = JSON.parse(JSON.stringify(content.answers));

    content.noViewer = 0;
    content.noLiked = 0;
    content.noComment = 0;
    //using more share quiz
    content.published = true;
    content.privacy = true;

    //default
    content.isActive = true;
    content.isDeleted = false;
    content.createdOn = new Date();
    content.createdBy = this.userLogin.uid;
    content.updatedOn = new Date();
    content.author = this.userLogin;
    content.visibility = this.visibility;

    return this.contentRef.add({ ...content }).then((docRef) => {
      const syncData = { id: content.id, ...content };
      this.sync(syncData);

      return docRef.id;
    });
  }

  update(content: Quiz): Promise<void> {
    content.answers = JSON.parse(JSON.stringify(content.answers));

    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    //console.log("QuizContentService/update:", content);

    return this.contentRef
      .doc(content.id)
      .update({
        type: content.type,
        title: content.title,
        question: content.question ? content.question : "",
        answers: content.answers,
        valueAnswer: content.valueAnswer ? content.valueAnswer : "",
        resultAnswers: content.resultAnswers ? content.resultAnswers : [],
        explain: content.explain ? content.explain : "",
        tags: content.tags ? content.tags : [],
        updatedOn: updatedOn,
        updatedBy: updatedBy,
        author: this.userLogin,
      })
      .then((docRef) => {
        const syncData = { id: content.id, ...content };
        this.sync(syncData);

        return docRef;
      });
  }

  delete(id: string): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;
    return this.contentRef.doc(id).update({
      isDeleted: true,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
    });
  }

  sync(content) {
    content.createdOn ? delete content.createdOn : null;
    content.updatedOn ? delete content.updatedOn : null;
    content.question ? delete content.question : null;
    content.answers ? delete content.answers : null;
    content.explain ? delete content.explain : null;

    // content.answers = JSON.stringify(content.answers);
    content.resultAnswers = JSON.stringify(content.resultAnswers);

    content.tags = JSON.stringify(content.tags);
    content.fsId = content.id ? content.id : "";

    if (environment.syncLive) {
      this.http.post(this.baseUrl + "/Quizzes", content).subscribe({
        next: () => {},
        error: (err) => {},
      });
    }
  }

  //Test Quiz
  loadQuestion(id: string, exerciseUserId?: string) {
    const callable = this.fns.httpsCallable("quiz-loadQuestion");
    return callable({
      id: id,
      exerciseUserId: exerciseUserId ? exerciseUserId : "",
    });
  }

  submitAnswer(quiz: Quiz) {
    const id = quiz.id;
    let submitAns = "";

    if (quiz.type == "input" || quiz.type == "document") {
      submitAns = quiz.inputAnswer ? quiz.inputAnswer.trim() : "";
    } else {
      submitAns = quiz.selectedAnswers
        ? JSON.stringify(quiz.selectedAnswers)
        : "";
    }

    const callable = this.fns.httpsCallable("quiz-submitAnswer");
    return callable({
      id: id,
      exerciseUserId: quiz.exerciseUserId ? quiz.exerciseUserId : "",
      exerciseUserQuizId: quiz.exerciseUserQuizId
        ? quiz.exerciseUserQuizId
        : "",
      submitAns: submitAns,
    });
  }

  //Sync ExerciseUserQuiz
  createAttempt(content) {
    content.id = content.id ? content.id : "";
    content.exerciseUserId = content.exerciseUserId
      ? content.exerciseUserId
      : "";
    content.quizId = content.quizId ? content.quizId : "";
    content.createdBy = content.createdBy ? content.createdBy : "";

    content.tags = content.tags ? JSON.stringify(content.tags) : "";
    content.answers = content.answers ? JSON.stringify(content.answers) : "";
    content.resultAnswers = content.resultAnswers
      ? JSON.stringify(content.resultAnswers)
      : "";
    content.selectedAnswers = content.selectedAnswers
      ? JSON.stringify(content.selectedAnswers)
      : "";

    return this.http.post(this.baseUrl + "/ExerciseUserQuizzes", content).pipe(
      map((response) => {
        return response;
      })
    );
  }

  updateAttempt(content) {
    content.id = content.id ? content.id : "";
    content.selectedAnswers = content.selectedAnswers
      ? JSON.stringify(content.selectedAnswers)
      : "";
    content.inputAnswer = content.inputAnswer ? content.inputAnswer : "";
    content.resultAnswers = content.resultAnswers
      ? JSON.stringify(content.resultAnswers)
      : "";
    content.valueAnswer = content.valueAnswer ? content.valueAnswer : "";
    content.explain = content.explain ? content.explain : "";

    return this.http.put(this.baseUrl + "/ExerciseUserQuizzes", content).pipe(
      map((response) => {
        return response;
      })
    );
  }
}
